import { useParams } from 'react-router-dom'
import { useProject, useProjectTasks, useProjectProgress, useProjectMembers } from '@/hooks/useProject'

const BRIDGENEXT_PURPLE = '#5F00AA'

export default function ProjectTracker() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <div>Project not found</div>

  const { data: project, isLoading: projectLoading } = useProject(id)
  const { data: tasks = [], isLoading: tasksLoading } = useProjectTasks(id)
  const { data: progress, isLoading: progressLoading } = useProjectProgress(id)
  const { data: members = [], isLoading: membersLoading } = useProjectMembers(id)

  if (projectLoading || tasksLoading || progressLoading || membersLoading) {
    return <div className="text-center py-8">Loading project...</div>
  }

  if (!project) {
    return <div>Project not found</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
        <p className="text-gray-600 mt-1">Acquired Company: {project.acquiredCompanyName}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Tasks */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tasks ({tasks.length})</h2>

            {tasks.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No tasks in this project</p>
            ) : (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div key={task.id} className="border border-gray-200 rounded p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      </div>
                      <div className="ml-4 flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            task.priority === 'CRITICAL'
                              ? 'bg-red-100 text-red-800'
                              : task.priority === 'HIGH'
                                ? 'bg-orange-100 text-orange-800'
                                : task.priority === 'MEDIUM'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {task.priority}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            task.status === 'DONE'
                              ? 'bg-green-100 text-green-800'
                              : task.status === 'IN_PROGRESS'
                                ? 'bg-blue-100 text-blue-800'
                                : task.status === 'BLOCKED'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {task.status}
                        </span>
                      </div>
                    </div>
                    {task.ownerName && (
                      <p className="text-xs text-gray-500 mt-3">Owner: {task.ownerName}</p>
                    )}
                    {task.dueDate && (
                      <p className="text-xs text-gray-500">Due: {new Date(task.dueDate).toLocaleDateString()}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Progress & Members */}
        <div className="space-y-6">
          {/* Progress Card */}
          {progress && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Progress</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Overall</span>
                    <span className="text-sm font-bold text-gray-900">
                      {progress.done} / {progress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full"
                      style={{
                        backgroundColor: BRIDGENEXT_PURPLE,
                        width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{progress.done}</p>
                    <p className="text-xs text-gray-600">Complete</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{progress.inProgress}</p>
                    <p className="text-xs text-gray-600">In Progress</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{progress.blocked}</p>
                    <p className="text-xs text-gray-600">Blocked</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">
                      {progress.total - progress.done - progress.inProgress - progress.blocked}
                    </p>
                    <p className="text-xs text-gray-600">Not Started</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Members Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Team ({members.length})</h2>
            <div className="space-y-2">
              {members.slice(0, 5).map((member) => (
                <div key={member.id} className="text-sm py-2 border-b border-gray-100 last:border-0">
                  <p className="font-medium text-gray-900">{member.memberName}</p>
                  <p className="text-xs text-gray-500">{member.memberEmail}</p>
                </div>
              ))}
              {members.length > 5 && (
                <p className="text-xs text-gray-500 pt-2">+{members.length - 5} more members</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
